import { gql } from '@/graphql';

/**
 * Every operation the app sends, in one file.
 *
 * Codegen scans for the `gql` tag, so keeping the documents together makes it
 * obvious what the client actually asks for and stops the same query being
 * written three slightly different ways in three components.
 */

// --- Auth ------------------------------------------------------------------

export const USER_FIELDS = gql(`
  fragment UserFields on User {
    id
    email
    name
    role
    restaurantId
    active
    lastLoginAt
  }
`);

export const ME_QUERY = gql(`
  query Me {
    me { ...UserFields }
    myRestaurant { ...RestaurantFields }
  }
`);

export const LOGIN_MUTATION = gql(`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      authenticated
      user { ...UserFields }
    }
  }
`);

export const REGISTER_MUTATION = gql(`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      authenticated
      user { ...UserFields }
    }
  }
`);

export const LOGOUT_MUTATION = gql(`
  mutation Logout {
    logout
  }
`);

export const CHANGE_PASSWORD_MUTATION = gql(`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`);

export const TEAM_MEMBERS_QUERY = gql(`
  query TeamMembers($restaurantId: ID) {
    teamMembers(restaurantId: $restaurantId) {
      ...UserFields
    }
  }
`);

export const INVITE_STAFF_MUTATION = gql(`
  mutation InviteStaff($input: InviteStaffInput!) {
    inviteStaff(input: $input) {
      ...UserFields
    }
  }
`);

// --- Dining / Floor --------------------------------------------------------

export const DINING_SETTINGS_QUERY = gql(`
  query DiningSettings($restaurantId: ID) {
    diningSettings(restaurantId: $restaurantId) {
      orderingEnabled
      defaultInstanceId
      allowGuestOrder
      sessionIdleMinutes
    }
  }
`);

export const UPDATE_DINING_SETTINGS_MUTATION = gql(`
  mutation UpdateDiningSettings($input: UpdateDiningSettingsInput!, $restaurantId: ID) {
    updateDiningSettings(input: $input, restaurantId: $restaurantId) {
      orderingEnabled
      defaultInstanceId
      allowGuestOrder
      sessionIdleMinutes
    }
  }
`);

export const RESTAURANT_TABLES_QUERY = gql(`
  query RestaurantTables($restaurantId: ID) {
    restaurantTables(restaurantId: $restaurantId) {
      id
      restaurantId
      label
      zone
      seats
      active
      sortOrder
      token
      publicUrl
      instanceId
      createdAt
      updatedAt
    }
  }
`);

export const CREATE_TABLES_MUTATION = gql(`
  mutation CreateTables($input: CreateTablesInput!, $restaurantId: ID) {
    createTables(input: $input, restaurantId: $restaurantId) {
      id
      label
      zone
      token
      publicUrl
      active
      sortOrder
    }
  }
`);

export const UPDATE_TABLE_MUTATION = gql(`
  mutation UpdateTable($tableId: ID!, $input: UpdateTableInput!, $restaurantId: ID) {
    updateTable(tableId: $tableId, input: $input, restaurantId: $restaurantId) {
      id
      label
      zone
      seats
      active
      token
      publicUrl
      instanceId
    }
  }
`);

export const SET_TABLE_ACTIVE_MUTATION = gql(`
  mutation SetTableActive($tableId: ID!, $active: Boolean!, $restaurantId: ID) {
    setTableActive(tableId: $tableId, active: $active, restaurantId: $restaurantId) {
      id
      active
    }
  }
`);

export const FLOOR_SNAPSHOT_QUERY = gql(`
  query FloorSnapshot($restaurantId: ID) {
    floorSnapshot(restaurantId: $restaurantId) {
      generatedAt
      tables {
        attention
        pendingRoundCount
        openCallCount
        table {
          id
          label
          zone
          seats
          token
          publicUrl
          active
        }
        session {
          id
          status
          openedAt
          openedVia
          totalMinor
          rounds {
            id
            status
            source
            submittedAt
            totalMinor
            lines {
              menuItemId
              name
              variantLabel
              unitPrice
              qty
              notes
            }
          }
          openCalls {
            id
            type
            status
            createdAt
          }
        }
      }
    }
  }
`);

export const OPEN_TABLE_SESSION_MUTATION = gql(`
  mutation OpenTableSession($tableId: ID!, $restaurantId: ID) {
    openTableSession(tableId: $tableId, restaurantId: $restaurantId) {
      id
      status
      tableId
    }
  }
`);

export const CLOSE_TABLE_SESSION_MUTATION = gql(`
  mutation CloseTableSession($sessionId: ID!, $restaurantId: ID) {
    closeTableSession(sessionId: $sessionId, restaurantId: $restaurantId) {
      id
      status
      closedAt
    }
  }
`);

export const ACKNOWLEDGE_ORDER_ROUND_MUTATION = gql(`
  mutation AcknowledgeOrderRound($roundId: ID!, $restaurantId: ID) {
    acknowledgeOrderRound(roundId: $roundId, restaurantId: $restaurantId) {
      id
      status
    }
  }
`);

export const ADVANCE_ORDER_ROUND_MUTATION = gql(`
  mutation AdvanceOrderRound($roundId: ID!, $restaurantId: ID) {
    advanceOrderRound(roundId: $roundId, restaurantId: $restaurantId) {
      id
      status
    }
  }
`);

export const CANCEL_ORDER_ROUND_MUTATION = gql(`
  mutation CancelOrderRound($roundId: ID!, $restaurantId: ID) {
    cancelOrderRound(roundId: $roundId, restaurantId: $restaurantId) {
      id
      status
    }
  }
`);

export const ADD_WAITER_ORDER_ROUND_MUTATION = gql(`
  mutation AddWaiterOrderRound($input: AddWaiterRoundInput!, $restaurantId: ID) {
    addWaiterOrderRound(input: $input, restaurantId: $restaurantId) {
      id
      status
      totalMinor
    }
  }
`);

export const RESOLVE_SERVICE_CALL_MUTATION = gql(`
  mutation ResolveServiceCall($callId: ID!, $restaurantId: ID) {
    resolveServiceCall(callId: $callId, restaurantId: $restaurantId) {
      id
      status
    }
  }
`);

export const SUBMIT_GUEST_ORDER_ROUND_MUTATION = gql(`
  mutation SubmitGuestOrderRound($input: SubmitGuestOrderInput!) {
    submitGuestOrderRound(input: $input) {
      id
      status
      totalMinor
      submittedAt
    }
  }
`);

export const GUEST_CALL_SERVICE_MUTATION = gql(`
  mutation GuestCallService($input: GuestServiceCallInput!) {
    guestCallService(input: $input) {
      id
      type
      status
    }
  }
`);

export const GUEST_SESSION_SUMMARY_QUERY = gql(`
  query GuestSessionSummary($token: String!, $guestKey: String!) {
    guestSessionSummary(token: $token, guestKey: $guestKey) {
      id
      status
      totalMinor
      rounds {
        id
        status
        source
        submittedAt
        totalMinor
        lines {
          name
          variantLabel
          unitPrice
          qty
          notes
        }
      }
      openCalls {
        id
        type
        status
      }
    }
  }
`);

export const SET_STAFF_ACTIVE_MUTATION = gql(`
  mutation SetStaffActive($userId: ID!, $active: Boolean!) {
    setStaffActive(userId: $userId, active: $active) {
      ...UserFields
    }
  }
`);

// --- Restaurant ------------------------------------------------------------

export const RESTAURANT_FIELDS = gql(`
  fragment RestaurantFields on Restaurant {
    id
    name
    slug
    tagline
    tier
    currency
    locale
    timezone
    foundedYear
    hours
    onboardingComplete
    brand {
      primaryColor
      secondaryColor
      colorOverrides
      displayFont
      bodyFont
      accentFont
      radiusScale
      typeScale
      logoUrl
      extractedPalette
    }
    contact {
      address
      phone
      website
      email
      instagram
      wifiName
      wifiPassword
    }
  }
`);

export const ALL_RESTAURANTS_QUERY = gql(`
  query AllRestaurants {
    restaurants {
      ...RestaurantFields
      ownerId
      ownerActive
      ownerEmail
      ownerName
      billing {
        status
        currentPeriodEnd
        cancelAtPeriodEnd
        suspended
      }
    }
  }
`);

export const ADMIN_RESTAURANT_QUERY = gql(`
  query AdminRestaurant($id: ID!) {
    restaurant(id: $id) {
      ...RestaurantFields
      ownerId
      ownerActive
      ownerEmail
      ownerName
      createdAt
      billing {
        status
        currentPeriodEnd
        cancelAtPeriodEnd
        suspended
      }
    }
  }
`);

export const ALL_USERS_QUERY = gql(`
  query AllUsers {
    users {
      ...UserFields
      createdAt
    }
  }
`);

export const SET_USER_ACTIVE_MUTATION = gql(`
  mutation SetUserActive($userId: ID!, $active: Boolean!) {
    setUserActive(userId: $userId, active: $active) {
      id
      active
      email
      name
    }
  }
`);

export const ADMIN_SET_RESTAURANT_TIER_MUTATION = gql(`
  mutation AdminSetRestaurantTier($restaurantId: ID!, $tier: PlanTier!) {
    adminSetRestaurantTier(restaurantId: $restaurantId, tier: $tier) {
      ...RestaurantFields
      ownerId
      ownerActive
      ownerEmail
      ownerName
      billing {
        status
        currentPeriodEnd
        cancelAtPeriodEnd
        suspended
      }
    }
  }
`);

export const UPDATE_RESTAURANT_MUTATION = gql(`
  mutation UpdateRestaurant($input: UpdateRestaurantInput!) {
    updateRestaurant(input: $input) { ...RestaurantFields }
  }
`);

export const UPDATE_BRAND_MUTATION = gql(`
  mutation UpdateBrandTheme($input: UpdateBrandInput!) {
    updateBrandTheme(input: $input) { ...RestaurantFields }
  }
`);

// --- Menus -----------------------------------------------------------------

export const MENU_ITEM_FIELDS = gql(`
  fragment MenuItemFields on MenuItem {
    id
    name
    description
    price
    compareAtPrice
    image
    imageAspect
    tags
    allergens
    featured
    spicyLevel
    calories
    portion
    available
    position
    variants { id name price }
    availability { daysOfWeek startTime endTime }
  }
`);

export const MENU_FIELDS = gql(`
  fragment MenuFields on Menu {
    id
    name
    title
    subtitle
    footnote
    archived
    totalItems
    updatedAt
    categories {
      id
      name
      description
      image
      position
      startsNewSection
      itemCount
      items { ...MenuItemFields }
    }
  }
`);

export const MENUS_QUERY = gql(`
  query Menus($includeArchived: Boolean, $restaurantId: ID) {
    menus(includeArchived: $includeArchived, restaurantId: $restaurantId) {
      id
      name
      title
      totalItems
      archived
      updatedAt
    }
  }
`);

export const MENU_QUERY = gql(`
  query Menu($menuId: ID!) {
    menu(menuId: $menuId) { ...MenuFields }
  }
`);

export const CREATE_MENU_MUTATION = gql(`
  mutation CreateMenu($input: CreateMenuInput!) {
    createMenu(input: $input) { ...MenuFields }
  }
`);

export const UPDATE_MENU_MUTATION = gql(`
  mutation UpdateMenu($menuId: ID!, $input: UpdateMenuInput!) {
    updateMenu(menuId: $menuId, input: $input) { ...MenuFields }
  }
`);

export const DELETE_MENU_MUTATION = gql(`
  mutation DeleteMenu($menuId: ID!) {
    deleteMenu(menuId: $menuId)
  }
`);

export const DUPLICATE_MENU_MUTATION = gql(`
  mutation DuplicateMenu($menuId: ID!) {
    duplicateMenu(menuId: $menuId) { ...MenuFields }
  }
`);

export const ADD_CATEGORY_MUTATION = gql(`
  mutation AddCategory($menuId: ID!, $input: MenuCategoryInput!) {
    addCategory(menuId: $menuId, input: $input) { ...MenuFields }
  }
`);

export const UPDATE_CATEGORY_MUTATION = gql(`
  mutation UpdateCategory($menuId: ID!, $categoryId: ID!, $input: MenuCategoryInput!) {
    updateCategory(menuId: $menuId, categoryId: $categoryId, input: $input) { ...MenuFields }
  }
`);

export const DELETE_CATEGORY_MUTATION = gql(`
  mutation DeleteCategory($menuId: ID!, $categoryId: ID!) {
    deleteCategory(menuId: $menuId, categoryId: $categoryId) { ...MenuFields }
  }
`);

export const REORDER_CATEGORIES_MUTATION = gql(`
  mutation ReorderCategories($menuId: ID!, $orderedIds: [ID!]!) {
    reorderCategories(menuId: $menuId, orderedIds: $orderedIds) { ...MenuFields }
  }
`);

export const ADD_ITEM_MUTATION = gql(`
  mutation AddItem($menuId: ID!, $categoryId: ID!, $input: MenuItemInput!) {
    addItem(menuId: $menuId, categoryId: $categoryId, input: $input) { ...MenuFields }
  }
`);

export const UPDATE_ITEM_MUTATION = gql(`
  mutation UpdateItem($menuId: ID!, $itemId: ID!, $input: MenuItemInput!) {
    updateItem(menuId: $menuId, itemId: $itemId, input: $input) { ...MenuFields }
  }
`);

export const DELETE_ITEM_MUTATION = gql(`
  mutation DeleteItem($menuId: ID!, $itemId: ID!) {
    deleteItem(menuId: $menuId, itemId: $itemId) { ...MenuFields }
  }
`);

export const REORDER_ITEMS_MUTATION = gql(`
  mutation ReorderItems($menuId: ID!, $categoryId: ID!, $orderedIds: [ID!]!) {
    reorderItems(menuId: $menuId, categoryId: $categoryId, orderedIds: $orderedIds) {
      ...MenuFields
    }
  }
`);

export const MOVE_ITEM_MUTATION = gql(`
  mutation MoveItem($menuId: ID!, $itemId: ID!, $targetCategoryId: ID!, $targetIndex: Int!) {
    moveItem(
      menuId: $menuId
      itemId: $itemId
      targetCategoryId: $targetCategoryId
      targetIndex: $targetIndex
    ) { ...MenuFields }
  }
`);

export const BULK_UPDATE_ITEMS_MUTATION = gql(`
  mutation BulkUpdateItems($menuId: ID!, $input: BulkItemUpdateInput!) {
    bulkUpdateItems(menuId: $menuId, input: $input) { ...MenuFields }
  }
`);

export const IMPORT_ITEMS_MUTATION = gql(`
  mutation ImportMenuItems($input: ImportMenuInput!) {
    importMenuItems(input: $input) {
      categoriesCreated
      itemsCreated
      itemsSkipped
      warnings
      menu { ...MenuFields }
    }
  }
`);

// --- Templates -------------------------------------------------------------

export const TEMPLATE_FIELDS = gql(`
  fragment TemplateFields on Template {
    id
    name
    description
    tags
    minTier
    coverImageUrl
    publishedVersionId
    publishedVersionNumber
    latestVersionNumber
    archived
    unlocked
    publishedFormats
    formatVariants {
      id
      label
      format
    }
    updatedAt
  }
`);

export const TEMPLATES_QUERY = gql(`
  query Templates($includeArchived: Boolean) {
    templates(includeArchived: $includeArchived) { ...TemplateFields }
  }
`);

export const PREVIEW_TEMPLATE_ON_MENU_QUERY = gql(`
  query PreviewTemplateOnMenu($templateId: ID!, $menuId: ID!, $variantId: String) {
    previewTemplateOnMenu(templateId: $templateId, menuId: $menuId, variantId: $variantId) {
      instanceId
      revision
      name
      restaurantName
      restaurantLogo
      elapsedMs
      book
      settings {
        kioskMode
        idleResetSeconds
        showPrices
        allowGuestSearch
        enablePageTearing
        showWatermark
        qrForeground
        qrBackground
        qrIncludeLogo
      }
    }
  }
`);

export const TEMPLATE_QUERY = gql(`
  query Template($templateId: ID!) {
    template(templateId: $templateId) { ...TemplateFields }
    templateVersions(templateId: $templateId) {
      id
      version
      status
      changelog
      publishedAt
      updatedAt
    }
  }
`);

export const TEMPLATE_VERSION_QUERY = gql(`
  query TemplateVersion($versionId: ID!) {
    templateVersion(versionId: $versionId) {
      id
      templateId
      version
      status
      document
      changelog
      publishedAt
      updatedAt
    }
  }
`);

export const CREATE_TEMPLATE_MUTATION = gql(`
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      template { ...TemplateFields }
      initialVersion { id version }
    }
  }
`);

export const UPDATE_TEMPLATE_MUTATION = gql(`
  mutation UpdateTemplate($templateId: ID!, $input: UpdateTemplateInput!) {
    updateTemplate(templateId: $templateId, input: $input) { ...TemplateFields }
  }
`);

export const REGENERATE_TEMPLATE_COVER_MUTATION = gql(`
  mutation RegenerateTemplateCover($templateId: ID!) {
    regenerateTemplateCover(templateId: $templateId) { ...TemplateFields }
  }
`);

export const SAVE_TEMPLATE_DRAFT_MUTATION = gql(`
  mutation SaveTemplateDraft($versionId: ID!, $document: JSON!, $changelog: String, $expectedUpdatedAt: String) {
    saveTemplateDraft(versionId: $versionId, document: $document, changelog: $changelog, expectedUpdatedAt: $expectedUpdatedAt) {
      id
      version
      status
      updatedAt
    }
  }
`);

export const PUBLISH_TEMPLATE_VERSION_MUTATION = gql(`
  mutation PublishTemplateVersion($versionId: ID!) {
    publishTemplateVersion(versionId: $versionId) {
      id
      version
      status
      publishedAt
    }
  }
`);

export const CREATE_TEMPLATE_DRAFT_MUTATION = gql(`
  mutation CreateTemplateDraft($versionId: ID!) {
    createTemplateDraft(versionId: $versionId) {
      id
      version
      status
    }
  }
`);

export const DUPLICATE_TEMPLATE_MUTATION = gql(`
  mutation DuplicateTemplate($templateId: ID!) {
    duplicateTemplate(templateId: $templateId) { ...TemplateFields }
  }
`);

export const ARCHIVE_TEMPLATE_MUTATION = gql(`
  mutation ArchiveTemplate($templateId: ID!, $archived: Boolean!) {
    archiveTemplate(templateId: $templateId, archived: $archived) { ...TemplateFields }
  }
`);

export const VALIDATE_TEMPLATE_QUERY = gql(`
  query ValidateTemplateDocument($document: JSON!, $templateId: ID, $version: Int) {
    validateTemplateDocument(document: $document, templateId: $templateId, version: $version) {
      valid
      issues { path message }
    }
  }
`);

// --- Instances -------------------------------------------------------------

export const INSTANCE_FIELDS = gql(`
  fragment InstanceFields on MenuInstance {
    id
    name
    menuId
    templateId
    templateVersionId
    variantId
    publicSlug
    publicUrl
    status
    seed
    revision
    publishedRevisionId
    viewCount
    availableUpgrades
    guestBehind
    hasCustomDesign
    designUpdatedAt
    publishedAt
    lastViewedAt
    updatedAt
    settings {
      kioskMode
      idleResetSeconds
      showPrices
      allowGuestSearch
      enablePageTearing
      showWatermark
      qrForeground
      qrBackground
      qrIncludeLogo
    }
    brandOverrides {
      primaryColor
      secondaryColor
      colorOverrides
      displayFont
      bodyFont
      accentFont
      radiusScale
      typeScale
    }
  }
`);

export const INSTANCE_DESIGN_FIELDS = gql(`
  fragment InstanceDesignFields on InstanceDesign {
    instanceId
    templateId
    templateVersionId
    document
    designUpdatedAt
    hasCustomDesign
    name
    menuId
    seed
    variantId
  }
`);

export const ENSURE_INSTANCE_DESIGN_MUTATION = gql(`
  mutation EnsureInstanceDesign($id: ID!) {
    ensureInstanceDesign(id: $id) { ...InstanceDesignFields }
  }
`);

export const SAVE_INSTANCE_DESIGN_MUTATION = gql(`
  mutation SaveInstanceDesign($id: ID!, $document: JSON!, $expectedUpdatedAt: String) {
    saveInstanceDesign(id: $id, document: $document, expectedUpdatedAt: $expectedUpdatedAt) {
      ...InstanceDesignFields
    }
  }
`);

export const RESET_INSTANCE_DESIGN_MUTATION = gql(`
  mutation ResetInstanceDesign($id: ID!) {
    resetInstanceDesign(id: $id) { ...InstanceFields }
  }
`);

export const INSTANCES_QUERY = gql(`
  query MenuInstances {
    menuInstances { ...InstanceFields }
  }
`);

export const INSTANCE_QUERY = gql(`
  query MenuInstance($id: ID!) {
    menuInstance(id: $id) { ...InstanceFields }
  }
`);

export const RENDER_INSTANCE_QUERY = gql(`
  query RenderMenuInstance($id: ID!) {
    renderMenuInstance(id: $id) {
      instanceId
      revision
      revisionId
      publishedAt
      name
      restaurantName
      restaurantLogo
      elapsedMs
      book
      settings {
        kioskMode
        idleResetSeconds
        showPrices
        allowGuestSearch
        enablePageTearing
        showWatermark
        qrForeground
        qrBackground
        qrIncludeLogo
      }
    }
  }
`);

export const PUBLIC_MENU_QUERY = gql(`
  query PublicMenu($slug: String!) {
    publicMenu(slug: $slug) {
      instanceId
      revision
      revisionId
      publishedAt
      name
      restaurantName
      restaurantLogo
      book
      settings {
        kioskMode
        idleResetSeconds
        showPrices
        allowGuestSearch
        enablePageTearing
        showWatermark
        qrForeground
        qrBackground
        qrIncludeLogo
      }
    }
  }
`);

export const CREATE_INSTANCE_MUTATION = gql(`
  mutation CreateMenuInstance($input: CreateInstanceInput!) {
    createMenuInstance(input: $input) { ...InstanceFields }
  }
`);

export const UPDATE_INSTANCE_MUTATION = gql(`
  mutation UpdateMenuInstance($id: ID!, $input: UpdateInstanceInput!) {
    updateMenuInstance(id: $id, input: $input) { ...InstanceFields }
  }
`);

export const RESHUFFLE_INSTANCE_MUTATION = gql(`
  mutation ReshuffleMenuInstance($id: ID!) {
    reshuffleMenuInstance(id: $id) { ...InstanceFields }
  }
`);

export const PUBLISH_INSTANCE_MUTATION = gql(`
  mutation PublishMenuInstance($id: ID!) {
    publishMenuInstance(id: $id) { ...InstanceFields }
  }
`);

export const UNPUBLISH_INSTANCE_MUTATION = gql(`
  mutation UnpublishMenuInstance($id: ID!) {
    unpublishMenuInstance(id: $id) { ...InstanceFields }
  }
`);

export const UPGRADE_INSTANCE_MUTATION = gql(`
  mutation UpgradeInstanceTemplate($id: ID!, $versionId: ID) {
    upgradeInstanceTemplate(id: $id, versionId: $versionId) { ...InstanceFields }
  }
`);

export const CHANGE_INSTANCE_TEMPLATE_MUTATION = gql(`
  mutation ChangeInstanceTemplate($id: ID!, $templateId: ID!, $variantId: String) {
    changeInstanceTemplate(id: $id, templateId: $templateId, variantId: $variantId) { ...InstanceFields }
  }
`);

export const DELETE_INSTANCE_MUTATION = gql(`
  mutation DeleteMenuInstance($id: ID!) {
    deleteMenuInstance(id: $id)
  }
`);

// --- Billing ---------------------------------------------------------------

export const SUBSCRIPTION_FIELDS = gql(`
  fragment SubscriptionFields on Subscription {
    id
    tier
    status
    currentPeriodStart
    currentPeriodEnd
    cancelAtPeriodEnd
    scheduledTier
    scheduledAt
    gracePeriodEndsAt
    suspended
    paymentsEnabled
  }
`);

export const BILLING_QUERY = gql(`
  query Billing {
    mySubscription { ...SubscriptionFields }
    plans {
      tier
      name
      monthlyPrice
      tagline
      features
      instanceLimit
      analytics
      removeWatermark
      customFonts
      exports
      current
    }
    billingHistory {
      id
      reason
      fromTier
      toTier
      amount
      currency
      description
      stripeInvoiceId
      createdAt
    }
    invoices {
      id
      amountPaid
      currency
      status
      createdAt
      hostedInvoiceUrl
      pdfUrl
    }
  }
`);

export const PLAN_CHANGE_QUOTE_QUERY = gql(`
  query PlanChangeQuote($tier: PlanTier!) {
    planChangeQuote(tier: $tier) {
      fromTier
      toTier
      direction
      remainingDays
      remainingValue
      creditedDays
      effectiveUntil
      deferred
      explanation
      chargedToday
    }
  }
`);

export const START_CHECKOUT_MUTATION = gql(`
  mutation StartCheckout($input: ChangePlanInput!) {
    startCheckout(input: $input) { url sessionId }
  }
`);

export const CHANGE_PLAN_MUTATION = gql(`
  mutation ChangePlan($input: ChangePlanInput!) {
    changePlan(input: $input) { ...SubscriptionFields }
  }
`);

export const SET_CANCEL_MUTATION = gql(`
  mutation SetCancelAtPeriodEnd($cancel: Boolean!) {
    setCancelAtPeriodEnd(cancel: $cancel) { ...SubscriptionFields }
  }
`);

export const BILLING_PORTAL_MUTATION = gql(`
  mutation BillingPortalUrl {
    billingPortalUrl
  }
`);

// --- Analytics -------------------------------------------------------------

export const ANALYTICS_QUERY = gql(`
  query Analytics($days: Int!, $instanceId: ID) {
    analytics(days: $days, instanceId: $instanceId) {
      available
      totalViews
      uniqueSessions
      medianPagesRead
      medianSessionSeconds
      daily { date views sessions }
      topItems { itemId name categoryName taps }
      topPages { pageIndex title views }
      breakdown { type count }
      searches
    }
  }
`);

// --- Media -----------------------------------------------------------------

export const ASSETS_QUERY = gql(`
  query Assets($purpose: AssetPurpose) {
    assets(purpose: $purpose) {
      id
      url
      width
      height
      mimeType
      sizeBytes
      purpose
      palette
      createdAt
      variants { url format width sizeBytes }
    }
  }
`);

export const CREATE_UPLOAD_MUTATION = gql(`
  mutation CreateUpload($input: CreateUploadInput!) {
    createUpload(input: $input) {
      uploadUrl
      objectKey
      expiresInSeconds
      maxBytes
    }
  }
`);

export const FINALIZE_UPLOAD_MUTATION = gql(`
  mutation FinalizeUpload($objectKey: String!, $purpose: AssetPurpose!, $originalName: String) {
    finalizeUpload(objectKey: $objectKey, purpose: $purpose, originalName: $originalName) {
      id
      url
      width
      height
      palette
      purpose
      variants { url format width sizeBytes }
    }
  }
`);

export const DELETE_ASSET_MUTATION = gql(`
  mutation DeleteAsset($assetId: ID!) {
    deleteAsset(assetId: $assetId)
  }
`);

export const FONT_FAMILIES_QUERY = gql(`
  query FontFamilies($search: String, $limit: Int) {
    fontFamilies(search: $search, limit: $limit) {
      family
      category
      weights
      hasItalic
      isVariable
      popularityRank
    }
  }
`);

export const PLATFORM_ASSETS_QUERY = gql(`
  query PlatformAssets {
    platformAssets {
      id
      url
      width
      height
      mimeType
      purpose
      createdAt
    }
  }
`);

// --- AI Design Engine ------------------------------------------------------

export const AI_DESIGN_STATUS_QUERY = gql(`
  query AiDesignStatus {
    aiDesignStatus {
      enabled
      provider
      dailyLimit
      remainingToday
      message
    }
  }
`);

export const AI_DESIGN_JOB_QUERY = gql(`
  query AiDesignJob($jobId: ID!) {
    aiDesignJob(jobId: $jobId) {
      id
      status
      kind
      brief
      concept
      clarifyingQuestions
      clarificationAnswers
      candidateDocument
      qualityWarnings
      validationIssues
      planSteps
      editOps
      pulseBefore
      pulseAfter
      errorMessage
      cancelled
      target
      variationIndex
      parentJobId
      tokenUsage { inputTokens outputTokens }
      createdAt
      updatedAt
    }
  }
`);

export const START_AI_DESIGN_JOB_MUTATION = gql(`
  mutation StartAiDesignJob($input: StartAiDesignJobInput!) {
    startAiDesignJob(input: $input) {
      id
      status
      clarifyingQuestions
      variationIndex
    }
  }
`);

export const START_AI_EDIT_JOB_MUTATION = gql(`
  mutation StartAiEditJob($input: StartAiEditJobInput!) {
    startAiEditJob(input: $input) {
      id
      status
      kind
      planSteps
      pulseBefore
    }
  }
`);

export const ANSWER_AI_CLARIFICATION_MUTATION = gql(`
  mutation AnswerAiClarification($input: AnswerAiClarificationInput!) {
    answerAiClarification(input: $input) {
      id
      status
    }
  }
`);

export const REFINE_AI_DESIGN_JOB_MUTATION = gql(`
  mutation RefineAiDesignJob($input: RefineAiDesignJobInput!) {
    refineAiDesignJob(input: $input) {
      id
      status
    }
  }
`);

export const CANCEL_AI_DESIGN_JOB_MUTATION = gql(`
  mutation CancelAiDesignJob($jobId: ID!) {
    cancelAiDesignJob(jobId: $jobId) {
      id
      status
      cancelled
    }
  }
`);

// --- Platform / admin ops --------------------------------------------------

export const PLATFORM_ENV_STATUS_QUERY = gql(`
  query PlatformEnvStatus {
    platformEnvStatus {
      summary {
        total
        set
        usingDefault
        empty
        missing
        needsAttention
      }
      variables {
        key
        category
        criticality
        description
        impact
        secret
        presence
        needsAttention
        displayValue
      }
    }
  }
`);

export const PLATFORM_STATS_QUERY = gql(`
  query PlatformStats($from: DateTime!, $to: DateTime!) {
    platformStats(from: $from, to: $to) {
      periodStart
      periodEnd
      totalRestaurants
      newRestaurantsInPeriod
      revenueInPeriod
      estimatedMrr
      activePaidSubscriptions
      pastDueCount
      paymentsEnabled
      currency
      restaurantsByTier {
        tier
        count
      }
      subscriptionsByStatus {
        status
        count
      }
      signupsByDay {
        date
        count
      }
      revenueByDay {
        date
        amount
      }
    }
  }
`);
